/**
 * Demo page for the landing hero and `/r/demo`: a fictional neighbourhood cafe. Nothing here
 * touches the network; drafts are assembled locally from the chosen facts so the flow can be
 * tried end to end. The real service writes drafts with the server pipeline.
 */
import type { Draft, DraftRequest, PublicPage } from "@/lib/api/types";

export const DEMO_SLUG = "demo";

export const DEMO_PAGE: PublicPage = {
  slug: DEMO_SLUG,
  name: "모래내 커피",
  subject_noun: "매장",
  industry: "restaurant",
  accent: "green",
  show_badge: true,
  status: "active",
  good_chips: [
    { id: "coffee", label: "커피 맛" },
    { id: "kind", label: "친절" },
    { id: "mood", label: "분위기" },
    { id: "dessert", label: "디저트" },
    { id: "parking", label: "주차" },
    { id: "quiet", label: "조용함" },
  ],
  bad_chips: [
    { id: "wait", label: "대기" },
    { id: "price", label: "가격" },
    { id: "seat", label: "자리" },
  ],
  questions: [
    {
      id: "visit_type",
      prompt: "어떻게 이용했어요?",
      required: false,
      options: [
        { id: "dine_in", label: "매장에서" },
        { id: "takeout", label: "포장해서" },
      ],
    },
    {
      id: "visit_count",
      prompt: "몇 번째 방문이에요?",
      required: false,
      options: [
        { id: "first", label: "처음 왔어요" },
        { id: "few", label: "두세 번째예요" },
        { id: "often", label: "자주 와요" },
      ],
    },
  ],
  platforms: [
    {
      id: "naver_place",
      name: "네이버 플레이스",
      link: "page",
      url: "https://map.naver.com/",
      steps: [
        "리뷰 탭에서 리뷰 쓰기를 누르세요",
        "영수증이나 결제 내역으로 방문을 인증하세요",
        "글 칸을 길게 눌러 붙여 넣으세요",
      ],
      body_max: 400,
      has_title: false,
    },
    {
      id: "kakao_map",
      name: "카카오맵",
      link: "page",
      url: "https://map.kakao.com/",
      steps: [
        "후기 탭에서 후기 작성을 누르세요",
        "별점을 고르고 글 칸에 붙여 넣으세요",
        "등록을 누르세요",
      ],
      body_max: null,
      has_title: false,
    },
    {
      id: "google_maps",
      name: "Google 지도",
      link: "page",
      url: "https://maps.google.com/",
      steps: ["리뷰 쓰기를 누르세요", "별점을 고르고 글 칸에 붙여 넣으세요", "게시를 누르세요"],
      body_max: null,
      has_title: false,
    },
  ],
  sponsored: false,
  disclose_ai: false,
  limits: { good_max: 3, bad_max: 2 },
};

type Phrase = { short: string; long: string };

const GOOD: Record<string, Phrase> = {
  coffee: { short: "커피가 맛있어요", long: "커피가 진하면서도 끝맛이 깔끔했어요" },
  kind: {
    short: "직원분들이 친절해요",
    long: "주문할 때 직원분이 원두를 친절하게 설명해 주셨어요",
  },
  mood: { short: "분위기가 편안해요", long: "조명이 따뜻해서 오래 앉아 있기 편했어요" },
  dessert: { short: "디저트도 맛있어요", long: "같이 시킨 디저트가 많이 달지 않아서 좋았어요" },
  parking: { short: "주차가 편해요", long: "가게 앞에 차를 댈 곳이 있어서 편했어요" },
  quiet: { short: "조용해서 좋아요", long: "음악 소리가 크지 않아서 이야기 나누기 좋았어요" },
};

const BAD: Record<string, Phrase> = {
  wait: {
    short: "주문이 밀려 조금 기다렸어요",
    long: "사람이 많을 때 가서 음료가 나오기까지 좀 기다렸어요",
  },
  price: { short: "가격은 조금 있는 편이에요", long: "가격은 근처 다른 곳보다 조금 높은 편이에요" },
  seat: {
    short: "자리가 넉넉하진 않아요",
    long: "자리가 많지 않아서 앉을 곳 찾기가 쉽지 않았어요",
  },
};

const OPENERS: Record<string, string> = {
  dine_in: "매장에서 마시고 왔어요.",
  takeout: "포장해서 마셨어요.",
  first: "처음 가 봤어요.",
  few: "두세 번째 방문이에요.",
  often: "자주 가는 곳이에요.",
};

/** Rotates which phrasing comes first so "다른 초안 보기" shows something different. */
function rotate<T>(list: T[], by: number): T[] {
  if (list.length === 0) return list;
  const k = by % list.length;
  return [...list.slice(k), ...list.slice(0, k)];
}

export function demoDrafts(req: DraftRequest, round = 0): Draft[] {
  const goods = rotate(
    req.good.map((id) => GOOD[id]).filter((p): p is Phrase => !!p),
    round
  );
  const bads = req.bad.map((id) => BAD[id]).filter((p): p is Phrase => !!p);
  const bright = req.tone === "bright";
  const end = bright ? "!" : ".";
  const openers = Object.values(req.answers)
    .map((a) => OPENERS[a])
    .filter(Boolean)
    .join(" ");

  const short = [
    goods.map((g) => g.short).join(", ") + end,
    bads.length ? `${bads.map((b) => b.short).join(", ")}.` : "",
  ]
    .filter(Boolean)
    .join(" ");

  const mid = [
    openers,
    `${goods.map((g) => g.long).join(". ")}${end}`,
    bads.length ? `다만 ${bads[0]?.short}.` : "",
    bright ? "또 갈게요!" : "또 들를 것 같아요.",
  ]
    .filter(Boolean)
    .join(" ");

  const long = [
    openers || "동네에 있어서 들러 봤어요.",
    ...goods.map((g, i) => (i === 0 && bright ? `${g.long}${end}` : `${g.long}.`)),
    ...bads.map((b) => `아쉬운 점은 ${b.long}.`),
    bright ? "그래도 다음에 또 가고 싶은 곳이에요!" : "그래도 다음에 또 갈 생각이에요.",
  ]
    .filter(Boolean)
    .join(" ");

  return [short, mid, long].map((text) => ({ title: null, text: text.replace(/\.\./g, ".") }));
}
